/**
 * Onboarding Redux Action Creators (thunks)
 */
import actions from './actions';
import { DataService } from '../../../config/dataService/dataService';
import HttpStatus from '../../../utility/status.enum';
import { fetchCmsContent } from '../../../redux/manageCms/actionCreator';

const {
  onboardingSessionBegin,
  onboardingSessionSuccess,
  onboardingSessionErr,
  onboardingStepSaveBegin,
  onboardingStepSaveSuccess,
  onboardingStepSaveErr,
  onboardingChatBegin,
  onboardingChatSuccess,
  onboardingChatErr,
  onboardingInitiateBegin,
  onboardingInitiateSuccess,
  onboardingInitiateErr,
  onboardingVerifyBegin,
  onboardingVerifySuccess,
  onboardingVerifyErr,
  onboardingResendBegin,
  onboardingResendSuccess,
  onboardingResendErr,
  onboardingCompleteBegin,
  onboardingCompleteSuccess,
  onboardingCompleteErr,
  onboardingPmsListBegin,
  onboardingPmsListSuccess,
  onboardingPmsListErr,
  onboardingPmsAuthBegin,
  onboardingPmsAuthSuccess,
  onboardingPmsAuthErr,
  onboardingMsgTemplatesBegin,
  onboardingMsgTemplatesSuccess,
  onboardingMsgTemplatesErr,
  onboardingQualifyBegin,
  onboardingQualifySuccess,
  onboardingQualifyErr,
  onboardingStripeSetupBegin,
  onboardingStripeSetupSuccess,
  onboardingStripeSetupErr,
  onboardingStripeConfirmBegin,
  onboardingStripeConfirmSuccess,
  onboardingStripeConfirmErr,
  onboardingAgreeDefaultsBegin,
  onboardingAgreeDefaultsSuccess,
  onboardingAgreeDefaultsErr,
  onboardingPartnerCoverageBegin,
  onboardingPartnerCoverageSuccess,
  onboardingPartnerCoverageErr,
  onboardingSyncProgressBegin,
  onboardingSyncProgressSuccess,
  onboardingSyncProgressErr,
  onboardingLocalFaveAddBegin,
  onboardingLocalFaveAddSuccess,
  onboardingLocalFaveAddErr,
  onboardingLocalFaveUpdateBegin,
  onboardingLocalFaveUpdateSuccess,
  onboardingLocalFaveUpdateErr,
  onboardingLocalFaveDeleteBegin,
  onboardingLocalFaveDeleteSuccess,
  onboardingLocalFaveDeleteErr,
  onboardingLocalFaveReorderBegin,
  onboardingLocalFaveReorderSuccess,
  onboardingLocalFaveReorderErr,
  onboardingGuidebookSectionSaveBegin,
  onboardingGuidebookSectionSaveSuccess,
  onboardingGuidebookSectionSaveErr,
  onboardingSetListingThemeBegin,
  onboardingSetListingThemeSuccess,
  onboardingSetListingThemeErr,
  onboardingTriggerListingSyncBegin,
  onboardingTriggerListingSyncSuccess,
  onboardingTriggerListingSyncErr,
  onboardingGuidebookPreviewBegin,
  onboardingGuidebookPreviewSuccess,
  onboardingGuidebookPreviewErr,
  onboardingThemesBegin,
  onboardingThemesSuccess,
  onboardingThemesErr,
  onboardingUpdateMsgTemplateBegin,
  onboardingUpdateMsgTemplateSuccess,
  onboardingUpdateMsgTemplateErr,
  onboardingActivateMessagingBegin,
  onboardingActivateMessagingSuccess,
  onboardingActivateMessagingErr,
  onboardingGuidebookStatusBegin,
  onboardingGuidebookStatusSuccess,
  onboardingGuidebookStatusErr,
  onboardingDisconnectPmsBegin,
  onboardingDisconnectPmsSuccess,
  onboardingDisconnectPmsErr,
  onboardingPmsConnectionStatusBegin,
  onboardingPmsConnectionStatusSuccess,
  onboardingPmsConnectionStatusErr,
  onboardingConnectPmsCredentialsBegin,
  onboardingConnectPmsCredentialsSuccess,
  onboardingConnectPmsCredentialsErr,
  onboardingIsUsHostBegin,
  onboardingIsUsHostSuccess,
  onboardingIsUsHostErr,
  onboardingSetStepDraft,
  onboardingCmsSet,
} = actions;

// ─── API endpoint constants ───────────────────────────────────────────────────
const OnboardingSessionAPIName             = '/onboarding/session';
const OnboardingStepSaveAPIName            = '/onboarding/step';
const OnboardingChatAPIName                = '/onboarding/chat';
const OnboardingInitiateAPIName            = '/onboarding/initiate';
const OnboardingVerifyPhoneAPIName         = '/onboarding/verify-phone';
const OnboardingResendOtpAPIName           = '/onboarding/resend-otp';
const OnboardingCompleteAPIName            = '/onboarding/complete';
const OnboardingPmsListAPIName             = '/onboarding/pms-list';
const OnboardingInitiatePmsAuthAPIName     = '/onboarding/initiate-pms-auth';
const OnboardingConnectPmsCredAPIName      = '/onboarding/connect-pms-credentials';
const OnboardingPmsConnectionStatusAPIName = '/onboarding/pms-connection-status';
const OnboardingDisconnectPmsAPIName       = '/onboarding/disconnect-pms';
const OnboardingGuidebookStatusAPIName     = '/onboarding/guidebook-status';
const OnboardingActivateMessagingAPIName   = '/onboarding/activate-messaging';
const OnboardingUpdateMsgSettingsAPIName   = '/onboarding/update-message-settings';
const OnboardingMsgTemplatesAPIName        = '/onboarding/message-templates';
const OnboardingThemesAPIName              = '/onboarding/themes';
const OnboardingAgreeDefaultsAPIName       = '/onboarding/agree-defaults';
const OnboardingPartnerCoverageAPIName     = '/onboarding/partner-coverage';
const OnboardingSyncProgressAPIName        = '/onboarding/listings-sync-progress';
const OnboardingGuidebookPreviewAPIName    = '/onboarding/guidebook-preview';
const OnboardingQualifyAPIName             = '/onboarding/qualify';
const OnboardingIsUsHostAPIName            = '/onboarding/is-us-host';
const OnboardingStripeSetupIntentAPIName   = '/onboarding/stripe/setup-intent';
const OnboardingStripeConfirmPmAPIName     = '/onboarding/stripe/confirm-payment-method';
const OnboardingSetListingThemeAPIName     = '/onboarding/set-listing-theme';
const OnboardingTriggerListingSyncAPIName  = '/onboarding/trigger-listing-sync';
const OnboardingListingsBaseAPIName        = '/onboarding/listings';
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the current onboarding session.
 * Requires JWT in cookie (set after verifyPhone). Used for session resume.
 * Returns current_step, step_data, completed_steps, and host fields.
 */
const getOnboardingSession = () => {
  return async (dispatch) => {
    dispatch(onboardingSessionBegin());
    try {
      const response = await DataService.get(OnboardingSessionAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingSessionErr(response?.data?.message));
      } else {
        dispatch(onboardingSessionSuccess(response?.data?.result));
        return response?.data?.result;
      }
    } catch (err) {
      dispatch(onboardingSessionErr(err));
    }
  };
};

const saveOnboardingStep = (stepSlug, stepData = {}) => {
  return async (dispatch) => {
    dispatch(onboardingStepSaveBegin());
    try {
      const response = await DataService.post(`${OnboardingStepSaveAPIName}/${stepSlug}`, stepData);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingStepSaveErr(response?.data?.message));
        return { ok: false };
      } else {
        dispatch(onboardingStepSaveSuccess(response?.data?.result));
        return { ok: true, data: response?.data?.result };
      }
    } catch (err) {
      dispatch(onboardingStepSaveErr(err));
      return { ok: false };
    }
  };
};

const sendChatMessage = (message, stepSlug, sessionSlug, chatHistory = []) => {
  return async (dispatch) => {
    dispatch(onboardingChatBegin());
    try {
      const response = await DataService.post(OnboardingChatAPIName, {
        message,
        step_slug: stepSlug,
        session_slug: sessionSlug,
        chat_history: chatHistory,
      });
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingChatErr(response?.data?.message));
      } else {
        dispatch(onboardingChatSuccess(response?.data?.result));
        return response?.data?.result;
      }
    } catch (err) {
      dispatch(onboardingChatErr(err));
    }
  };
};

const initiateOnboarding = (name, email, phoneNumber, countryCode) => {
  return async (dispatch) => {
    dispatch(onboardingInitiateBegin());
    try {
      const response = await DataService.post(OnboardingInitiateAPIName, { name, email, phone_number: phoneNumber, country_code: countryCode });
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingInitiateErr(response?.data?.message));
        return response?.data;
      } else {
        dispatch(onboardingInitiateSuccess(response?.data?.result));
        return response?.data;
      }
    } catch (err) {
      dispatch(onboardingInitiateErr(err));
    }
  };
};

const verifyOnboardingPhone = (authCode, otpCode) => {
  return async (dispatch) => {
    dispatch(onboardingVerifyBegin());
    try {
      const response = await DataService.post(OnboardingVerifyPhoneAPIName, {
        auth_code: authCode,
        otp_code: otpCode,
      });
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingVerifyErr(response?.data?.message));
        return response?.data;
      } else {
        dispatch(onboardingVerifySuccess(response?.data?.result));
        return response?.data;
      }
    } catch (err) {
      dispatch(onboardingVerifyErr(err));
    }
  };
};

const resendOtpAction = (authCode) => {
  return async (dispatch) => {
    dispatch(onboardingResendBegin());
    try {
      const response = await DataService.post(OnboardingResendOtpAPIName, {
        auth_code: authCode,
      });
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingResendErr(response?.data?.message));
      } else {
        dispatch(onboardingResendSuccess(response?.data?.result));
        return response?.data;
      }
    } catch (err) {
      dispatch(onboardingResendErr(err));
    }
  };
};

const completeOnboarding = () => {
  return async (dispatch) => {
    dispatch(onboardingCompleteBegin());
    try {
      const response = await DataService.post(OnboardingCompleteAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingCompleteErr(response?.data?.message));
      } else {
        dispatch(onboardingCompleteSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingCompleteErr(err));
    }
  };
};

const fetchPmsList = () => {
  return async (dispatch) => {
    dispatch(onboardingPmsListBegin());
    try {
      const response = await DataService.get(OnboardingPmsListAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingPmsListErr(response?.data?.message));
      } else {
        dispatch(onboardingPmsListSuccess(response?.data?.result));
        return response?.data?.result;
      }
    } catch (err) {
      dispatch(onboardingPmsListErr(err));
    }
  };
};

const initiatePmsOAuth = (pmsSlug) => {
  return async (dispatch) => {
    dispatch(onboardingPmsAuthBegin());
    try {
      const response = await DataService.post(OnboardingInitiatePmsAuthAPIName, { pms_slug: pmsSlug });
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingPmsAuthErr(response?.data?.message));
      } else {
        dispatch(onboardingPmsAuthSuccess(response?.data?.result));
        return response?.data?.result;
      }
    } catch (err) {
      dispatch(onboardingPmsAuthErr(err));
    }
  };
};

const connectPmsCredentials = (data) => {
  return async (dispatch) => {
    dispatch(onboardingConnectPmsCredentialsBegin());
    try {
      const response = await DataService.post(OnboardingConnectPmsCredAPIName, data);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingConnectPmsCredentialsErr(response?.data?.message));
      } else {
        dispatch(onboardingConnectPmsCredentialsSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingConnectPmsCredentialsErr(err));
    }
  };
};

const getPmsConnectionStatus = () => {
  return async (dispatch) => {
    dispatch(onboardingPmsConnectionStatusBegin());
    try {
      const response = await DataService.get(OnboardingPmsConnectionStatusAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingPmsConnectionStatusErr(response?.data?.message));
      } else {
        dispatch(onboardingPmsConnectionStatusSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingPmsConnectionStatusErr(err));
    }
  };
};

const disconnectPms = () => {
  return async (dispatch) => {
    dispatch(onboardingDisconnectPmsBegin());
    try {
      const response = await DataService.post(OnboardingDisconnectPmsAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingDisconnectPmsErr(response?.data?.message));
      } else {
        dispatch(onboardingDisconnectPmsSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingDisconnectPmsErr(err));
    }
  };
};

const getGuidebookStatus = () => {
  return async (dispatch) => {
    dispatch(onboardingGuidebookStatusBegin());
    try {
      const response = await DataService.get(OnboardingGuidebookStatusAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingGuidebookStatusErr(response?.data?.message));
      } else {
        dispatch(onboardingGuidebookStatusSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingGuidebookStatusErr(err));
    }
  };
};

const activateMessaging = () => {
  return async (dispatch) => {
    dispatch(onboardingActivateMessagingBegin());
    try {
      const response = await DataService.post(OnboardingActivateMessagingAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingActivateMessagingErr(response?.data?.message));
      } else {
        dispatch(onboardingActivateMessagingSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingActivateMessagingErr(err));
    }
  };
};

const updateMessageTemplate = (payload) => {
  return async (dispatch) => {
    dispatch(onboardingUpdateMsgTemplateBegin());
    try {
      const response = await DataService.post(OnboardingUpdateMsgSettingsAPIName, payload);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingUpdateMsgTemplateErr(response?.data?.message));
      } else {
        dispatch(onboardingUpdateMsgTemplateSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingUpdateMsgTemplateErr(err));
    }
  };
};

const fetchMessageTemplates = () => {
  return async (dispatch) => {
    dispatch(onboardingMsgTemplatesBegin());
    try {
      const response = await DataService.get(OnboardingMsgTemplatesAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingMsgTemplatesErr(response?.data?.message));
      } else {
        dispatch(onboardingMsgTemplatesSuccess(response?.data?.result));
        return response?.data?.result;
      }
    } catch (err) {
      dispatch(onboardingMsgTemplatesErr(err));
    }
  };
};

const getOnboardingThemes = () => {
  return async (dispatch) => {
    dispatch(onboardingThemesBegin());
    try {
      const response = await DataService.get(OnboardingThemesAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingThemesErr(response?.data?.message));
      } else {
        dispatch(onboardingThemesSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingThemesErr(err));
    }
  };
};

const fetchAgreeDefaults = () => {
  return async (dispatch) => {
    dispatch(onboardingAgreeDefaultsBegin());
    try {
      const response = await DataService.get(OnboardingAgreeDefaultsAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingAgreeDefaultsErr(response?.data?.message));
      } else {
        dispatch(onboardingAgreeDefaultsSuccess(response?.data?.result));
        return response?.data?.result;
      }
    } catch (err) {
      dispatch(onboardingAgreeDefaultsErr(err));
    }
  };
};

const fetchPartnerCoverage = () => {
  return async (dispatch) => {
    dispatch(onboardingPartnerCoverageBegin());
    try {
      const response = await DataService.get(OnboardingPartnerCoverageAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingPartnerCoverageErr(response?.data?.message));
      } else {
        dispatch(onboardingPartnerCoverageSuccess(response?.data?.result));
        return response?.data?.result;
      }
    } catch (err) {
      dispatch(onboardingPartnerCoverageErr(err));
    }
  };
};

const fetchListingsSyncProgress = (params = {}) => {
  return async (dispatch) => {
    dispatch(onboardingSyncProgressBegin());
    try {
      // Build query string from params (expected_count, after)
      const query = new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')
        )
      ).toString();
      const url = query
        ? `/onboarding/listings-sync-progress?${query}`
        : '/onboarding/listings-sync-progress';
      const response = await DataService.get(url);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingSyncProgressErr(response?.data?.message));
      } else {
        dispatch(onboardingSyncProgressSuccess(response?.data?.result));
        return response?.data?.result;
      }
    } catch (err) {
      dispatch(onboardingSyncProgressErr(err));
    }
  };
};

const getGuidebookPreview = (listingSlug) => {
  return async (dispatch) => {
    dispatch(onboardingGuidebookPreviewBegin());
    try {
      const response = await DataService.get(`${OnboardingGuidebookPreviewAPIName}/${listingSlug}`);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingGuidebookPreviewErr(response?.data?.message));
      } else {
        dispatch(onboardingGuidebookPreviewSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingGuidebookPreviewErr(err));
    }
  };
};

const submitQualification = (data) => {
  return async (dispatch) => {
    dispatch(onboardingQualifyBegin());
    try {
      const response = await DataService.post(OnboardingQualifyAPIName, data);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingQualifyErr(response?.data?.message));
        return response?.data;
      } else {
        dispatch(onboardingQualifySuccess(response?.data?.result));
        return response?.data;
      }
    } catch (err) {
      dispatch(onboardingQualifyErr(err));
    }
  };
};

const checkIsUsHost = () => {
  return async (dispatch) => {
    dispatch(onboardingIsUsHostBegin());
    try {
      const response = await DataService.get(OnboardingIsUsHostAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingIsUsHostErr(response?.data?.message));
      } else {
        dispatch(onboardingIsUsHostSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingIsUsHostErr(err));
    }
  };
};

const createStripeSetupIntent = () => {
  return async (dispatch) => {
    dispatch(onboardingStripeSetupBegin());
    try {
      const response = await DataService.post(OnboardingStripeSetupIntentAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingStripeSetupErr(response?.data?.message));
        return response?.data;
      } else {
        dispatch(onboardingStripeSetupSuccess(response?.data?.result));
        return response?.data;
      }
    } catch (err) {
      dispatch(onboardingStripeSetupErr(err));
    }
  };
};

const confirmStripePaymentMethod = (paymentMethodId) => {
  return async (dispatch) => {
    dispatch(onboardingStripeConfirmBegin());
    try {
      const response = await DataService.post(OnboardingStripeConfirmPmAPIName, {
        payment_method_id: paymentMethodId,
      });
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingStripeConfirmErr(response?.data?.message));
        return response?.data;
      } else {
        dispatch(onboardingStripeConfirmSuccess(response?.data?.result));
        return response?.data;
      }
    } catch (err) {
      dispatch(onboardingStripeConfirmErr(err));
    }
  };
};

const setListingTheme = (listingSlug, themeSlug) => {
  return async (dispatch) => {
    dispatch(onboardingSetListingThemeBegin());
    try {
      const response = await DataService.post(OnboardingSetListingThemeAPIName, {
        listing_slug: listingSlug,
        theme_slug:   themeSlug,
      });
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingSetListingThemeErr(response?.data?.message));
      } else {
        dispatch(onboardingSetListingThemeSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingSetListingThemeErr(err));
    }
  };
};

const triggerListingSync = () => async (dispatch) => {
  dispatch(onboardingTriggerListingSyncBegin());
  try {
    const response = await DataService.post(OnboardingTriggerListingSyncAPIName, {});
    dispatch(onboardingTriggerListingSyncSuccess(response?.data?.result));
  } catch {
    dispatch(onboardingTriggerListingSyncErr(null));
    // Silent — cron dispatcher is the fallback if this fails
  }
};

const addLocalFave = (listingSlug, data) => {
  return async (dispatch) => {
    dispatch(onboardingLocalFaveAddBegin());
    try {
      const response = await DataService.post(`${OnboardingListingsBaseAPIName}/${listingSlug}/locals`, data);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingLocalFaveAddErr(response?.data?.message));
      } else {
        dispatch(onboardingLocalFaveAddSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingLocalFaveAddErr(err));
    }
  };
};

const updateLocalFave = (listingSlug, faveSlug, data) => {
  return async (dispatch) => {
    dispatch(onboardingLocalFaveUpdateBegin());
    try {
      const response = await DataService.put(`${OnboardingListingsBaseAPIName}/${listingSlug}/locals/${faveSlug}`, data);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingLocalFaveUpdateErr(response?.data?.message));
      } else {
        dispatch(onboardingLocalFaveUpdateSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingLocalFaveUpdateErr(err));
    }
  };
};

const deleteLocalFave = (listingSlug, faveSlug) => {
  return async (dispatch) => {
    dispatch(onboardingLocalFaveDeleteBegin());
    try {
      const response = await DataService.delete(`${OnboardingListingsBaseAPIName}/${listingSlug}/locals/${faveSlug}`);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingLocalFaveDeleteErr(response?.data?.message));
      } else {
        dispatch(onboardingLocalFaveDeleteSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingLocalFaveDeleteErr(err));
    }
  };
};

const reorderLocalFaves = (listingSlug, orderedSlugs) => {
  return async (dispatch) => {
    dispatch(onboardingLocalFaveReorderBegin());
    try {
      const response = await DataService.post(
        `${OnboardingListingsBaseAPIName}/${listingSlug}/locals/reorder`,
        { ordered_slugs: orderedSlugs },
      );
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingLocalFaveReorderErr(response?.data?.message));
      } else {
        dispatch(onboardingLocalFaveReorderSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingLocalFaveReorderErr(err));
    }
  };
};

const saveGuidebookSection = (listingSlug, sectionType, content) => {
  return async (dispatch) => {
    dispatch(onboardingGuidebookSectionSaveBegin());
    try {
      const response = await DataService.post(
        `${OnboardingListingsBaseAPIName}/${listingSlug}/guidebook-section`,
        { section_type: sectionType, content },
      );
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(onboardingGuidebookSectionSaveErr(response?.data?.message));
      } else {
        dispatch(onboardingGuidebookSectionSaveSuccess(response?.data?.result));
      }
      return response?.data;
    } catch (err) {
      dispatch(onboardingGuidebookSectionSaveErr(err));
    }
  };
};

const prefetchCmsKeys = (keys) => {
  return async (dispatch) => {
    try {
      const result = await dispatch(fetchCmsContent({ keys }));
      const items = Array.isArray(result?.result) ? result.result : [];
      if (items.length === 0) return;
      const data = {};
      items.forEach((item) => {
        if (item?.key) data[item.key] = item.value ?? '';
      });
      dispatch(onboardingCmsSet(data));
      try {
        const stored = JSON.parse(localStorage.getItem('hg_onboarding_cms') || '{}');
        localStorage.setItem('hg_onboarding_cms', JSON.stringify({ ...stored, ...data }));
      } catch {}
    } catch {}
  };
};

const setStepDraft = (slug, data) => (dispatch) => {
  dispatch(onboardingSetStepDraft(slug, data));
};

export {
  getOnboardingSession,
  saveOnboardingStep,
  sendChatMessage,
  initiateOnboarding,
  verifyOnboardingPhone,
  resendOtpAction,
  completeOnboarding,
  fetchPmsList,
  initiatePmsOAuth,
  connectPmsCredentials,
  getPmsConnectionStatus,
  disconnectPms,
  fetchMessageTemplates,
  updateMessageTemplate,
  activateMessaging,
  getGuidebookStatus,
  getOnboardingThemes,
  fetchAgreeDefaults,
  fetchPartnerCoverage,
  fetchListingsSyncProgress,
  getGuidebookPreview,
  setListingTheme,
  submitQualification,
  checkIsUsHost,
  createStripeSetupIntent,
  confirmStripePaymentMethod,
  triggerListingSync,
  addLocalFave,
  updateLocalFave,
  deleteLocalFave,
  reorderLocalFaves,
  saveGuidebookSection,
  setStepDraft,
  prefetchCmsKeys,
};
